import { useState } from 'react';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import { listFinalDocumentation } from '../../api/finalDocumentation';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import {
  FinalDocumentationCompletion,
  FinalDocumentationRow,
} from '../../types/finalDocumentation';
import { FinalDocumentChecklistDialog } from './FinalDocumentChecklistDialog';
import { FinalDocumentProgressChip } from './FinalDocumentProgressChip';

/** Operations → Final Documentation Checklist (Phase 95), the step before
 * Ready to Fly: students heading for departure (visa approved / Ready to
 * Fly / in a batch) with their checklist progress. Click a row to review. */
export const FinalDocumentationPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState('');
  const [completion, setCompletion] = useState<FinalDocumentationCompletion>('ALL');
  const [open, setOpen] = useState<{ id: number; name: string } | null>(null);

  const listQuery = useQuery({
    queryKey: [
      'final-documentation',
      paginationModel.page,
      paginationModel.pageSize,
      search,
      completion,
      selectedBranchId,
    ],
    queryFn: () =>
      listFinalDocumentation({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
        completion,
        branchId: selectedBranchId,
      }),
    placeholderData: (prev) => prev,
  });
  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));
  const openRow = (row: FinalDocumentationRow) =>
    setOpen({ id: row.studentId, name: `${row.student.firstName} ${row.student.lastName}` });

  const columns: GridColDef<FinalDocumentationRow>[] = [
    {
      field: 'studentNumber',
      headerName: 'Student #',
      width: 120,
      valueGetter: (_v, row) => row.student.studentNumber,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => `${row.student.firstName} ${row.student.lastName}`,
    },
    {
      field: 'university',
      headerName: 'University',
      width: 180,
      valueGetter: (_v, row) => row.university?.name ?? '—',
    },
    {
      field: 'intake',
      headerName: 'Intake',
      width: 130,
      valueGetter: (_v, row) => row.intake?.name ?? '—',
    },
    {
      field: 'batch',
      headerName: 'Batch',
      width: 130,
      valueGetter: (_v, row) => row.batch?.batchNumber ?? '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Chip size="small" label={String(params.row.status).replace(/_/g, ' ')} />
      ),
    },
    {
      field: 'finalDocumentation',
      headerName: 'Checklist',
      width: 210,
      sortable: false,
      renderCell: (params) => (
        <FinalDocumentProgressChip progress={params.row.finalDocumentation} />
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={FactCheckIcon}
        color="#F5A623"
        title="Final Documentation Checklist"
        subtitle="Final documents for each student, uploaded and reviewed before Ready to Fly. An item is complete once its document is approved."
      />
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <TextField
          placeholder="Search name or student #…"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Checklist"
          sx={{ minWidth: 160 }}
          value={completion}
          onChange={(e) => {
            setCompletion(e.target.value as FinalDocumentationCompletion);
            resetPage();
          }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="INCOMPLETE">Incomplete</MenuItem>
          <MenuItem value="COMPLETE">Complete</MenuItem>
        </TextField>
      </Stack>

      {listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load the checklist list.
        </Alert>
      ) : (
        <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{ toolbar: TableToolbar }}
            localeText={{ noRowsLabel: 'No students are heading for departure yet.' }}
            disableRowSelectionOnClick
            onRowClick={(params) => openRow(params.row)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}
      <FinalDocumentChecklistDialog student={open} onClose={() => setOpen(null)} />
    </Box>
  );
};
